import {
  ArrowUpIcon,
  MagnifyingGlassIcon,
  MicrophoneIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';

import { useCommandMenu } from './useCommandMenu';

interface ChatWelcomeProps {
  onExampleClick: (query: string) => void;
  onSendMessage: (message: string) => void;
  newMessage: string;
  setNewMessage: (message: string) => void;
  mode?: 'query' | 'agent';
  autoFocus?: boolean;
  isPraetorianUser?: boolean;
  onModeChange: (mode: 'query' | 'agent') => void;
}

const ChatWelcome: React.FC<ChatWelcomeProps> = ({
  onExampleClick,
  onSendMessage,
  newMessage,
  setNewMessage,
  mode = 'query',
  autoFocus = false,
  isPraetorianUser = false,
  onModeChange,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [showTipsMenu, setShowTipsMenu] = useState(false);
  const recognitionRef = useRef<any>(null);
  const commandMenuRef = useRef<HTMLDivElement>(null);
  const tipsMenuRef = useRef<HTMLDivElement>(null);

  const tips = [
    {
      icon: '@',
      label: 'Search Assets',
      description: 'Type @ followed by asset name or DNS',
    },
    {
      icon: '#',
      label: 'Search Risks',
      description: 'Type # followed by risk name',
    },
    {
      icon: '&',
      label: 'Search Webpages',
      description: 'Type & followed by URL',
    },
    {
      icon: '/',
      label: 'Search Capabilities',
      description: 'Type / followed by capability name',
    },
  ];

  const handleEntitySearch = (
    type: 'asset' | 'risk' | 'webpage' | 'capability',
    query: string
  ) => {
    // This won't be called anymore since we're just showing examples
  };

  const {
    showCommandMenu,
    filteredCommands,
    executeCommandIfMatch,
    shouldAutoComplete,
    selectCommand,
    replaceCurrentWord,
    menuType,
  } = useCommandMenu(
    newMessage,
    isPraetorianUser,
    onModeChange,
    () => setNewMessage(''),
    handleEntitySearch
  );

  // Auto-focus on mount if requested
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  // Close tips menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        showTipsMenu &&
        !target.closest('.tips-menu-container') &&
        !target.closest('.tips-menu-button')
      ) {
        setShowTipsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTipsMenu]);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition =
        (window as any).SpeechRecognition ||
        (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = false;
        recognitionRef.current.lang = 'en-US';

        recognitionRef.current.onresult = (event: any) => {
          const transcript = event.results[0][0].transcript;
          // Append to existing message or set new message
          const updatedMessage = newMessage
            ? newMessage + ' ' + transcript
            : transcript;
          setNewMessage(updatedMessage);
          setIsRecording(false);
        };

        recognitionRef.current.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setIsRecording(false);
          // Don't show alert for 'no-speech' - just silently stop
          if (event.error !== 'no-speech' && event.error !== 'aborted') {
            console.warn('Speech recognition failed:', event.error);
          }
        };

        recognitionRef.current.onend = () => {
          setIsRecording(false);
        };
      }
    }
  }, [newMessage, setNewMessage]);

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      alert(
        'Speech recognition is not supported in your browser. Please use Chrome, Safari, or Edge.'
      );
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  const examples = [
    {
      label: 'Exploitable Vulnerabilities',
      prompt:
        'Identify exploitable vulnerabilities across our attack surface that threat actors could abuse. Prioritize by severity and likelihood of exploitation. For each critical finding, provide the affected assets, attack vectors, and recommended remediation to prevent breach.',
    },
    {
      label: 'Trends & Changes',
      prompt:
        "Show me what's changed in the last 7 days. Highlight new assets discovered, newly exploitable CVEs, configuration drifts, or unusual security events. Compare our exposure trend to the previous week and flag any risk areas that are growing worse.",
    },
    {
      label: 'Exposure Management',
      prompt:
        'Analyze our continuous exposure management posture. Show attack paths that could lead to compromise, vulnerable assets requiring immediate attention, and recommended actions to reduce our attack surface. Focus on prevention-first strategies that minimize breach likelihood.',
    },
  ];

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [newMessage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newMessage.trim();
    if (!trimmed) return;

    // Auto-complete if there's exactly one matching command
    if (shouldAutoComplete) {
      selectCommand(filteredCommands[0].command);
      return;
    }

    // Check if it's a command
    if (executeCommandIfMatch(trimmed)) {
      return;
    }

    // Regular message
    onSendMessage(trimmed);
  };

  return (
    <div className="flex h-full items-center justify-center px-8">
      <div className="w-full max-w-4xl">
        <div className="mb-8 text-center">
          <h1 className="text-chariot-text-primary mb-6 text-4xl font-semibold">
            Chariot AI Assistant
          </h1>

          {/* Centered Input */}
          <form onSubmit={handleSubmit}>
            <div className="relative mx-auto max-w-3xl">
              <div
                onClick={() => textareaRef.current?.focus()}
                className={`bg-chariot-background-primary flex cursor-text items-center gap-3 rounded-full border px-4 py-3 transition-colors ${
                  mode === 'agent'
                    ? 'border-chariot-stroke-high hover:border-chariot-stroke-high'
                    : 'border-chariot-stroke hover:border-chariot-brand-primary'
                }`}
              >
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    setShowTipsMenu(prev => !prev);
                  }}
                  className="tips-menu-button text-chariot-text-secondary hover:text-chariot-text-primary flex-shrink-0 transition-colors"
                  title="Show search shortcuts"
                >
                  <MagnifyingGlassIcon className="size-5" />
                </button>
                <textarea
                  ref={textareaRef}
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  placeholder="Ask anything about your attack surface"
                  className="text-chariot-text-primary placeholder-chariot-text-secondary flex-1 resize-none bg-transparent text-sm focus:outline-none"
                  rows={1}
                  style={{
                    minHeight: '24px',
                    maxHeight: '120px',
                  }}
                />
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    toggleRecording();
                  }}
                  className={`flex size-8 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
                    isRecording
                      ? 'animate-pulse bg-red-500 text-white hover:bg-red-600'
                      : 'text-chariot-text-secondary hover:text-chariot-text-primary'
                  }`}
                  title={isRecording ? 'Stop recording' : 'Voice input'}
                >
                  <MicrophoneIcon className="size-4" />
                </button>
                <button
                  type="submit"
                  disabled={!newMessage.trim()}
                  onClick={e => e.stopPropagation()}
                  className={`flex size-8 flex-shrink-0 items-center justify-center rounded-full text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    mode === 'agent'
                      ? 'bg-chariot-status-high hover:bg-chariot-status-high/90'
                      : 'bg-chariot-brand-primary hover:bg-chariot-brand-primary/90'
                  }`}
                >
                  <ArrowUpIcon className="size-4" />
                </button>
              </div>

              {/* Command/Search menu - show below input */}
              {showCommandMenu &&
                filteredCommands.length > 0 &&
                (menuType === 'search' || isPraetorianUser) && (
                  <div
                    ref={commandMenuRef}
                    className="command-menu bg-chariot-background-primary border-chariot-stroke absolute top-full right-0 left-0 z-50 mt-2 max-h-80 overflow-hidden rounded-lg border shadow-lg"
                  >
                    <div className="max-h-80 overflow-y-auto p-2">
                      {filteredCommands.map((cmd, index) => (
                        <button
                          key={`${cmd.command}-${index}`}
                          type="button"
                          onClick={() => {
                            // For entity search (@ or #), replace with JSON data if available
                            if (menuType === 'search' && cmd.data) {
                              setNewMessage(
                                replaceCurrentWord(cmd.command, cmd.data)
                              );
                              textareaRef.current?.focus();
                            }
                            // For search without data, just replace text
                            else if (menuType === 'search') {
                              setNewMessage(replaceCurrentWord(cmd.command));
                              textareaRef.current?.focus();
                            }
                            // For commands, execute action
                            else {
                              selectCommand(cmd.command);
                            }
                          }}
                          className="hover:bg-chariot-background-secondary w-full rounded px-3 py-2 text-left transition-colors"
                        >
                          <div className="text-chariot-text-primary text-sm font-medium">
                            {cmd.command}
                          </div>
                          <div className="text-chariot-text-secondary text-xs">
                            {cmd.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              {/* Tips menu popover */}
              {showTipsMenu && (
                <div className="tips-menu-container bg-chariot-background-primary border-chariot-stroke absolute bottom-full left-0 z-50 mb-2 w-80 overflow-hidden rounded-lg border shadow-lg">
                  <div className="p-3">
                    <div className="text-chariot-text-primary mb-3 text-sm font-semibold">
                      Quick Search Shortcuts
                    </div>
                    <div className="space-y-2">
                      {tips.map((tip, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setNewMessage(
                              newMessage +
                                (newMessage && !newMessage.endsWith(' ')
                                  ? ' '
                                  : '') +
                                tip.icon
                            );
                            textareaRef.current?.focus();
                            setShowTipsMenu(false);
                          }}
                          className="hover:bg-chariot-background-secondary w-full rounded px-3 py-2 text-left transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <code className="bg-chariot-background-secondary flex-shrink-0 rounded px-2 py-1 font-mono text-sm">
                              {tip.icon}
                            </code>
                            <div className="min-w-0 flex-1">
                              <div className="text-chariot-text-primary text-sm font-medium">
                                {tip.label}
                              </div>
                              <div className="text-chariot-text-secondary text-xs">
                                {tip.description}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="mt-2 flex items-center justify-center gap-4">
          <p className="text-chariot-text-secondary text-sm font-bold">
            Starter Hints:
          </p>
          <div className="flex flex-wrap gap-2">
            {examples.map((example, index) => (
              <button
                key={index}
                onClick={() => onExampleClick(example.prompt)}
                className="border-chariot-stroke hover:border-chariot-brand-primary text-chariot-text-secondary hover:text-chariot-text-primary rounded-full border-2 border-dashed px-4 py-2 text-sm whitespace-nowrap transition-colors"
              >
                {example.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatWelcome;
